import {
	getFormProps,
	getInputProps,
	getTextareaProps,
	useForm,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { useEffect, useRef } from 'react'
import { data, Form, useActionData, useNavigation } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { toast } from 'sonner'
import { z } from 'zod'
import { ErrorList, Field, TextareaField } from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { type Route } from './+types/contact.ts'

const ContactFormSchema = z.object({
	name: z.string().min(2, 'Name must be at least 2 characters'),
	email: z.string().email('Please enter a valid email address'),
	studio: z.string().optional(),
	subject: z.string().min(3, 'Subject must be at least 3 characters'),
	message: z.string().min(10, 'Message must be at least 10 characters'),
})

// Spam detection utilities
function isGibberish(text: string): boolean {
	if (!text || text.length < 5) return false

	// Check consonant ratio - gibberish often has unusual consonant patterns
	const consonants = text.replace(
		/[^bcdfghjklmnpqrstvwxyzBCDFGHJKLMNPQRSTVWXYZ]/g,
		'',
	).length
	const letters = text.replace(/[^a-zA-Z]/g, '').length
	if (letters > 0 && consonants / letters > 0.75) return true

	// Check for lack of spaces in long text (real sentences have spaces)
	if (text.length > 15 && !text.includes(' ')) return true

	// Check for random character sequences (no common letter patterns)
	const words = text.split(/\s+/)
	const suspiciousWords = words.filter((word) => {
		if (word.length < 8) return false
		// Check for unlikely letter combinations
		const unlikelyPatterns = /[bcdfghjklmnpqrstvwxyz]{5,}|[aeiou]{4,}/i
		return unlikelyPatterns.test(word)
	})
	if (suspiciousWords.length >= 2) return true

	// Check for mixed case gibberish (LiKeThIs pattern throughout)
	const mixedCaseRatio = (text.match(/[a-z][A-Z]|[A-Z][a-z][A-Z]/g) || [])
		.length
	if (mixedCaseRatio > text.length / 10) return true

	return false
}

function looksLikeSpam(data: {
	name: string
	email: string
	subject: string
	message: string
	studio?: string
}): boolean {
	// Check each field for gibberish
	if (isGibberish(data.name)) return true
	if (isGibberish(data.subject)) return true
	if (isGibberish(data.message)) return true
	if (data.studio && isGibberish(data.studio)) return true

	// Real names typically have spaces or are reasonably short
	if (data.name.length > 20 && !data.name.includes(' ')) return true

	// Check for suspicious email patterns (not the domain, but the local part)
	const emailLocal = data.email.split('@')[0]
	if (emailLocal && isGibberish(emailLocal)) return true

	// Message should have some punctuation or be conversational
	const hasNoPunctuation = !/[.,!?;:]/.test(data.message)
	const hasFewSpaces =
		(data.message.match(/\s/g) || []).length < data.message.length / 20
	if (hasNoPunctuation && hasFewSpaces && data.message.length > 20) return true

	return false
}

export const meta: Route.MetaFunction = () => [
	{ title: 'Contact Us | DanceMakers' },
	{
		name: 'description',
		content:
			"Get in touch with DanceMakers. We're here to answer your questions about conventions, competitions, scholarships, and more.",
	},
]

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()

	await checkHoneypot(formData)

	// Secondary honeypot - "website" field that should be empty
	const website = formData.get('website')
	if (website && String(website).length > 0) {
		console.log('Spam detected: website honeypot filled', { website })
		// Silently reject
		return data({
			result: { status: 'success' as const, initialValue: {} },
			success: true,
		})
	}

	// Time-based spam check - bots submit too fast
	const timestamp = formData.get('_timestamp')
	if (timestamp) {
		const elapsed = Date.now() - Number(timestamp)
		if (elapsed < 3000) {
			// Less than 3 seconds = likely bot
			console.log('Spam detected: form submitted too quickly', { elapsed })
			// Silently reject - don't give bots feedback
			return data({
				result: { status: 'success' as const, initialValue: {} },
				success: true,
			})
		}
	}

	const submission = parseWithZod(formData, { schema: ContactFormSchema })

	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply(), success: false },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { name, email, studio, subject, message } = submission.value

	// Content-based spam detection
	if (looksLikeSpam({ name, email, subject, message, studio })) {
		console.log('Spam detected: content looks like gibberish', {
			name,
			email,
			subject,
		})
		// Silently reject
		return data({
			result: { status: 'success' as const, initialValue: {} },
			success: true,
		})
	}

	// Send the email
	const result = await sendEmail({
		to: ['info@dancemakersinc.com', 'corey@dancemakersinc.com'],
		subject: `[Contact Form] ${subject}`,
		html: `
			<h2>New Contact Form Submission</h2>
			<p><strong>Name:</strong> ${name}</p>
			<p><strong>Email:</strong> ${email}</p>
			${studio ? `<p><strong>Studio:</strong> ${studio}</p>` : ''}
			<p><strong>Subject:</strong> ${subject}</p>
			<hr />
			<p><strong>Message:</strong></p>
			<p>${message.replace(/\n/g, '<br />')}</p>
		`,
		text: `
New Contact Form Submission

Name: ${name}
Email: ${email}
${studio ? `Studio: ${studio}\n` : ''}Subject: ${subject}

Message:
${message}
		`,
	})

	if (result.status === 'error') {
		return data(
			{
				result: submission.reply({
					formErrors: ['Failed to send message. Please try again.'],
				}),
				success: false,
			},
			{ status: 500 },
		)
	}

	return data({
		result: submission.reply({ resetForm: true }),
		success: true,
	})
}

export default function ContactRoute() {
	const actionData = useActionData<typeof action>()
	const navigation = useNavigation()
	const isSubmitting = navigation.state === 'submitting'
	const hasShownToast = useRef(false)

	const [form, fields] = useForm({
		id: 'contact-form',
		constraint: getZodConstraint(ContactFormSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ContactFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	// Show success toast and prevent showing it multiple times
	useEffect(() => {
		if (actionData?.success && !hasShownToast.current) {
			hasShownToast.current = true
			toast.success('Message Sent!', {
				description: "Thank you for reaching out. We'll get back to you soon.",
			})
		}
		// Reset when actionData changes (new submission attempt)
		if (!actionData?.success) {
			hasShownToast.current = false
		}
	}, [actionData])

	return (
		<div className="min-h-screen pt-12 md:pt-16">
			{/* Hero Section */}
			<section className="container px-6 pb-8 pt-12 md:px-16 md:pb-10 md:pt-16">
				<div className="max-w-3xl">
					<p className="mb-3 text-xs font-medium uppercase tracking-[0.2em] text-primary">
						Get in Touch
					</p>
					<h1 className="mb-6 font-display text-3xl font-bold uppercase leading-tight md:text-4xl lg:text-5xl">
						Contact Us
					</h1>
					<p className="text-sm leading-relaxed text-muted-foreground">
						Have questions about convention, competition, or anything else? We'd
						love to hear from you. Fill out the form below and we'll get back to
						you as soon as possible.
					</p>
				</div>
			</section>

			{/* Main Content */}
			<section className="bg-muted/30 py-10 md:py-16">
				<div className="container px-6 md:px-16">
					<div className="grid max-w-7xl items-start gap-8 lg:grid-cols-2 lg:gap-16">
						{/* Form Column */}
						<div className="order-2 lg:order-1">
							<div className="border border-muted-foreground/30 bg-background p-6 sm:p-8">
								<h2 className="mb-6 font-display text-xl font-bold uppercase sm:text-2xl">
									Send Us a Message
								</h2>

								<Form method="POST" {...getFormProps(form)}>
									<HoneypotInputs />
									{/* Time-based spam protection - bots fill forms too fast */}
									<input type="hidden" name="_timestamp" value={Date.now()} />
									{/* Secondary honeypot - hidden "website" field that bots love to fill */}
									<div
										className="absolute -left-[9999px] h-0 overflow-hidden opacity-0"
										aria-hidden="true"
									>
										<label htmlFor="website">Website (leave empty)</label>
										<input
											type="text"
											id="website"
											name="website"
											tabIndex={-1}
											autoComplete="off"
										/>
									</div>

									<div className="space-y-1">
										<Field
											labelProps={{ children: 'Name *' }}
											inputProps={{
												...getInputProps(fields.name, { type: 'text' }),
												placeholder: 'Your full name',
												autoComplete: 'name',
											}}
											errors={fields.name.errors}
										/>

										<Field
											labelProps={{ children: 'Email *' }}
											inputProps={{
												...getInputProps(fields.email, { type: 'email' }),
												placeholder: 'your@email.com',
												autoComplete: 'email',
											}}
											errors={fields.email.errors}
										/>

										<Field
											labelProps={{ children: 'Studio Name' }}
											inputProps={{
												...getInputProps(fields.studio, { type: 'text' }),
												placeholder: 'Your dance studio (optional)',
											}}
											errors={fields.studio.errors}
										/>

										<Field
											labelProps={{ children: 'Subject *' }}
											inputProps={{
												...getInputProps(fields.subject, { type: 'text' }),
												placeholder: 'What is this regarding?',
											}}
											errors={fields.subject.errors}
										/>

										<TextareaField
											labelProps={{ children: 'Message *' }}
											textareaProps={{
												...getTextareaProps(fields.message),
												placeholder: 'How can we help you?',
												rows: 5,
											}}
											errors={fields.message.errors}
										/>
									</div>

									<ErrorList id={form.errorId} errors={form.errors} />

									<StatusButton
										type="submit"
										status={isSubmitting ? 'pending' : (form.status ?? 'idle')}
										disabled={isSubmitting}
										className="mt-4 w-full"
									>
										{isSubmitting ? 'Sending...' : 'Send Message'}
									</StatusButton>
								</Form>
							</div>

							{/* Contact Info - Below form */}
							<div className="mt-8">
								<div className="mb-6 grid grid-cols-2 gap-6">
									<div>
										<h3 className="mb-2 font-display text-sm font-bold uppercase tracking-wider">
											Call Us
										</h3>
										<a
											href="tel:866-443-5300"
											className="text-sm text-primary hover:underline"
										>
											866-443-5300
										</a>
									</div>

									<div>
										<h3 className="mb-2 font-display text-sm font-bold uppercase tracking-wider">
											Registration
										</h3>
										<a
											href="mailto:info@dancemakersinc.com"
											className="break-all text-sm text-primary hover:underline"
										>
											info@dancemakersinc.com
										</a>
									</div>

									<div className="sm:col-span-2">
										<h3 className="mb-2 font-display text-sm font-bold uppercase tracking-wider">
											Photo/Video
										</h3>
										<a
											href="mailto:photobooth@dancemakersinc.com"
											className="break-all text-sm text-primary hover:underline"
										>
											photobooth@dancemakersinc.com
										</a>
									</div>
								</div>

								<div>
									<h3 className="mb-2 font-display text-sm font-bold uppercase tracking-wider">
										Mailing Address
									</h3>
									<address className="text-sm not-italic leading-relaxed text-muted-foreground">
										DanceMakers Inc.
										<br />
										PO BOX 1836
										<br />
										Charleston, SC 29402
									</address>
								</div>
							</div>
						</div>

						{/* Image Column - Second on mobile, right on desktop, sticky on desktop */}
						<div className="order-1 lg:sticky lg:top-24 lg:order-2">
							<div className="relative">
								<div className="absolute -inset-2 -z-10 bg-accent/10 md:-inset-3" />
								{/* Mobile image */}
								<img
									src="/img/contact-mobile.jpg"
									alt="DanceMakers faculty teaching"
									className="aspect-[4/3] w-full object-cover lg:hidden"
								/>
								{/* Desktop image */}
								<img
									src="/img/contact-desktop.jpg"
									alt="DanceMakers faculty teaching"
									className="hidden aspect-[3/4] w-full object-cover lg:block"
								/>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Quick Answers */}
			<section className="py-10 md:py-16">
				<div className="container px-6 text-center md:px-16">
					<h2 className="mb-4 font-display text-xl font-bold uppercase sm:text-2xl md:text-3xl">
						Looking for Quick Answers?
					</h2>
					<p className="mx-auto mb-6 max-w-2xl text-sm text-muted-foreground">
						Check out our information pages for details about convention,
						competition, scholarships, and awards.
					</p>
					<div className="flex flex-wrap justify-center gap-3 sm:gap-4">
						<a
							href="/convention-info"
							className="border border-primary px-4 py-2 text-xs font-medium uppercase tracking-wider text-primary transition-colors hover:bg-primary hover:text-background sm:px-6 sm:py-3 sm:text-sm"
						>
							Convention
						</a>
						<a
							href="/competition-info"
							className="border border-primary px-4 py-2 text-xs font-medium uppercase tracking-wider text-primary transition-colors hover:bg-primary hover:text-background sm:px-6 sm:py-3 sm:text-sm"
						>
							Competition
						</a>
						<a
							href="/scholarships"
							className="border border-primary px-4 py-2 text-xs font-medium uppercase tracking-wider text-primary transition-colors hover:bg-primary hover:text-background sm:px-6 sm:py-3 sm:text-sm"
						>
							Scholarships
						</a>
						<a
							href="/awards"
							className="border border-primary px-4 py-2 text-xs font-medium uppercase tracking-wider text-primary transition-colors hover:bg-primary hover:text-background sm:px-6 sm:py-3 sm:text-sm"
						>
							Awards
						</a>
					</div>
				</div>
			</section>
		</div>
	)
}
